import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FileInput } from "components/FileInput/FileInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { Preview } from "components/PreviewModal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { useSendCopyToVendorMutation } from "store/apis/taskMerchants";
import { useGetTeamMembersQuery } from "store/apis/teamSetting";
import { Merchant } from "types/Models/merchant";
import { BackendError } from "types/utils/error";
import { openDownloadUrl } from "util/openDownloadUrl";
import * as Yup from "yup";

const validationSchema = Yup.object({
  email: Yup.string()
    .required("Email is required")
    .matches(
      /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
      "Please enter a valid email"
    ),
  cc_emails: Yup.array()
    .of(Yup.string())
    .min(1, "Please select at least one CC email")
    .required("CC email is required"),
});

type SendCopyToVendorProps = {
  isOpen: boolean;
  close: () => void;
  groupId: string;
  merchant: Merchant;
  updateMerchant: () => void;
  season: string;
};

export const SendCopyToVendor = ({
  isOpen,
  close,
  groupId,
  merchant,
  updateMerchant,
  season,
}: SendCopyToVendorProps) => {
  const group = useCurrentGroupContext();
  const { form_status, email } = merchant || {};
  const { alertToast, successToast } = useToast();
  const previewModal = useModal();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const [sendCopyToVendor] = useSendCopyToVendorMutation();
  const recipients_document = merchant?.form_1099_document?.recipients_document;
  const { data: teamMembers, isLoading: isLoadingTeamMembers } =
    useGetTeamMembersQuery({ groupId }, { skip: !groupId });

  const allTeamEmails =
    teamMembers?.group_users
      ?.filter(
        ({ is_archived_user, is_verified }) => !is_archived_user && is_verified
      )
      ?.map(({ email }) => email) || [];

  const ccEmailOptions = allTeamEmails.map((email) => ({
    label: email,
    value: email,
  }));

  const onEmailSubmit = async ({
    email,
    cc_emails,
  }: {
    email: string;
    cc_emails: string[];
  }) => {
    try {
      if (recipients_document?.task_document_merchant_id) {
        await sendCopyToVendor({
          email,
          groupId,
          merchantDocMapId: recipients_document.task_document_merchant_id!,
          cc_emails: cc_emails.length > 0 ? cc_emails.join(",") : undefined,
        }).unwrap();
        updateMerchant();
        close();
        successToast({ message: "Email has been sent" });
      }
    } catch (error: any) {
      alertToast({ message: error?.response?.data?.error?.message });
    }
  };

  const onDownload = async () => {
    try {
      if (recipients_document?.uuid) {
        const { download_url } = await getPreviewUrl({
          groupId,
          fileId: recipients_document.uuid,
        }).unwrap();
        openDownloadUrl(download_url);
      }
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Portal>
          <Modal.Overlay />
          <Modal.Content size="large" asChild>
            <FormikForm
              onSubmit={onEmailSubmit}
              initialValues={{ email, cc_emails: allTeamEmails }}
              validationSchema={validationSchema}
              enableReinitialize
            >
              {({ isSubmitting, values }) => (
                <>
                  <Modal.Header>
                    <Modal.Title>Send copy to vendor</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body className="t-space-y-6">
                    <TextInput
                      label="To"
                      block
                      placeholder="Enter email of the contractor"
                      name="email"
                      required
                      customSize="regular"
                    />

                    <Combobox
                      name="cc_emails"
                      label="CC"
                      size="regular"
                      isMulti
                      withForm
                      placeholder="Add CC email addresses"
                      options={ccEmailOptions}
                      value={values.cc_emails.map((email: string) => ({
                        label: email,
                        value: email,
                      }))}
                    />

                    <div>
                      <div
                        className={classNames(
                          "t-mb-3 t-flex t-items-center t-justify-center t-h-full t-gap-3"
                        )}
                      >
                        <div className="t-w-full t-bg-purple-divider-gradient t-h-[1px] t-rotate-180" />
                        <div className="t-text-nowrap t-text-subtext-sm t-text-text-60">
                          Email Preview
                        </div>
                        <div className="t-w-full t-bg-purple-divider-gradient t-h-[1px]" />
                      </div>

                      <div className="t-border-dashed t-border-neutral-10 t-border t-bg-surface-lighter-grey t-rounded t-p-4">
                        <p className="t-text-text-30 t-text-body-sm">
                          <strong>Subject:</strong> 1099 Form Filed – Vendor
                          Copy from Inkle on behalf of {group.name}
                          <br />
                          <br /> <strong>Body:</strong>
                          <br />
                          <span>
                            Hi there,
                            <br />
                            <br />
                            Inkle has filed a 1099 form with the IRS on behalf
                            of {group.name} for the payments you received in{" "}
                            {season}
                            . Attached to this email is your vendor copy for
                            your records.
                            <br />
                            <br />
                            If you have any questions or need any
                            clarifications, feel free to reach out to us on this
                            email.
                            <br />
                            <br />
                            Many thanks, <br /> Team Inkle
                          </span>
                        </p>
                      </div>
                    </div>
                    <FileInput
                      name="recipientsDocument"
                      label="Attachment"
                      required
                      multiple={false}
                      onDownload={onDownload}
                      onFileClick={previewModal.open}
                      file={recipients_document || null}
                    />
                  </Modal.Body>

                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button>Cancel</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      isLoading={isSubmitting || isLoadingTeamMembers}
                      disabled={isSubmitting || isLoadingTeamMembers}
                    >
                      {form_status === "SENT" ? "Resend Email" : "Send Email"}
                    </Button>
                  </Modal.FooterButtonGroup>
                </>
              )}
            </FormikForm>
          </Modal.Content>
        </Modal.Portal>
      </Modal.Root>
      {previewModal.isOpen && (
        <Preview
          showModal={previewModal.isOpen}
          closeModal={previewModal.close}
          groupId={groupId}
          previewId={recipients_document?.uuid}
        />
      )}
    </>
  );
};
